import { Head, Link, router, useRemember } from "@inertiajs/react";
import { type CellTemplateProp, type ColumnRegular, type HyperFunc, RevoGrid, type VNode, defineCustomElements } from '@revolist/react-datagrid';
import SelectTypePlugin from '@revolist/revogrid-column-select';
import axios from "axios";
import {
  ArrowLeft,
  Building2,
  Send,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { DataTablePagination } from "@/components/data-table-pagination";
import { SearchableSelect } from "@/components/searchable-select";
import SelectedSuppliersChips from "@/components/selected-suppliers-chips";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { computeRevoGridHeight } from '@/helpers/revogrid';
import { useDataTable } from "@/hooks/use-data-table";
import { useRevoGridEdit } from '@/hooks/use-revogrid-edit';
import AppLayout from "@/layouts/app-layout";
import { newQuoteRequestPurchaseRequestPath, purchaseRequestPath, purchaseRequestsPath, sendInvitationsPurchaseRequestQuoteInvitationsPath, supplierPath } from "@/routes";
import type {
  BreadcrumbItem,
  PagyMetadata,
  PurchaseRequest,
  RansackFilters,
  Supplier,
} from "@/types";

defineCustomElements();

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Solicitudes Compras",
    href: purchaseRequestsPath(),
  },
];

interface Option {
  value: number;
  label: string;
}

interface LocationOption {
  value: string;
  label: string;
}

interface NewQuoteRequestProps {
  purchase_request: PurchaseRequest & {
    sector: {
      id: number;
      name: string;
    };
  };
  data: {
    suppliers: Supplier[];
    pagy: PagyMetadata;
  };
  filters: RansackFilters;
  cities: LocationOption[];
  states: LocationOption[];
}

export default function NewQuoteRequest({
  purchase_request,
  data,
  filters,
  cities,
  states,
}: NewQuoteRequestProps) {
  const { suppliers, pagy } = data;

  // Use shared data table logic for filtering and pagination
  const { isLoading, handleFilterChange, handlePageChange } = useDataTable({
    filters,
    pagy,
    // Prefer explicit typed path over window.location
    path: newQuoteRequestPurchaseRequestPath(purchase_request.id),
  });

  // Revogrid ref
  const gridRef = useRef<HTMLRevoGridElement>(null);

  // Category/subcategory options
  const [categories, setCategories] = useState<Option[]>([]);
  const [subcategories, setSubcategories] = useState<Option[]>([]);
  // Removed subcategoriesByCategory state - no longer needed since columns are read-only
  // subcategorySourceRef no longer needed - columns are read-only
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  // Supplier filtering state with debouncing
  const [nameSearch, setNameSearch] = useState(filters.name_cont ?? "");
  const [categoryFilter, setCategoryFilter] = useState(
    filters.category_id_eq ?? "",
  );
  const [subcategoryFilter, setSubcategoryFilter] = useState(
    filters.subcategory_id_eq ?? "",
  );
  const [citySearch, setCitySearch] = useState(filters.city_cont ?? "");
  const [stateSearch, setStateSearch] = useState(filters.state_cont ?? "");
  const [supplierTypeFilter, setSupplierTypeFilter] = useState(
    filters.supplier_type_eq ?? "",
  );

  const nameTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist supplier selection across pagination/filter changes.
  // useRemember saves local UI state in history, while preserveState keeps it across visits.
  // Store as array (history state must be serializable), derive Set for fast lookups.
  const [selectedSupplierIds, setSelectedSupplierIds] = useRemember<number[]>(
    [],
    `NewQuoteRequest:selectedSuppliers:${purchase_request.id}`,
  );
  const selectedSuppliers = new Set(selectedSupplierIds);

  // Minimal persistent map of known suppliers to display chips even when a
  // selected supplier is not present in the current page/filter results.
  const [knownSuppliers, setKnownSuppliers] = useRemember<Record<number, { id: number; name: string }>>(
    {},
    `NewQuoteRequest:knownSuppliers:${purchase_request.id}`,
  );

  // Merge current page suppliers into the known map (id -> name).
  useEffect(() => {
    if (!suppliers || suppliers.length === 0) return;
    setKnownSuppliers((prev) => {
      const next = { ...prev };
      for (const s of suppliers) {
        if (!next[s.id] || next[s.id].name !== s.name) {
          next[s.id] = { id: s.id, name: s.name };
        }
      }
      return next;
    });
  }, [suppliers, setKnownSuppliers]);

  // Build the suppliers list for chips: visible selected + missing selected from the known map
  const visibleSelected = suppliers.filter((s) => selectedSuppliers.has(s.id));
  const missingSelectedIds = Array.from(selectedSuppliers).filter(
    (id) => !visibleSelected.some((s) => s.id === id),
  );
  const supplemental = missingSelectedIds
    .map((id) => knownSuppliers[id])
    .filter(Boolean)
    // Cast minimal shape to Supplier — chip component only needs id and name.
    .map((info) => ({ id: info.id, name: info.name } as Supplier));
  const chipSuppliers: Supplier[] = [...visibleSelected, ...supplemental];

  // Sync local filter state with server filters
  useEffect(() => {
    setNameSearch(filters.name_cont ?? "");
    setCategoryFilter(filters.category_id_eq ?? "");
    setSubcategoryFilter(filters.subcategory_id_eq ?? "");
    setCitySearch(filters.city_cont ?? "");
    setStateSearch(filters.state_cont ?? "");
    setSupplierTypeFilter(filters.supplier_type_eq ?? "");
  }, [
    filters.name_cont,
    filters.category_id_eq,
    filters.subcategory_id_eq,
    filters.city_cont,
    filters.state_cont,
    filters.supplier_type_eq,
  ]);

  // Debounced name search
  useEffect(() => {
    if (nameTimeoutRef.current) {
      clearTimeout(nameTimeoutRef.current);
    }

    nameTimeoutRef.current = setTimeout(() => {
      if (nameSearch !== filters.name_cont) {
        handleFilterChange({ name_cont: nameSearch || undefined });
      }
    }, 500);

    return () => {
      if (nameTimeoutRef.current) {
        clearTimeout(nameTimeoutRef.current);
      }
    };
  }, [nameSearch, filters.name_cont, handleFilterChange]);

  // Debounced city search
  useEffect(() => {
    if (cityTimeoutRef.current) {
      clearTimeout(cityTimeoutRef.current);
    }

    cityTimeoutRef.current = setTimeout(() => {
      if (citySearch !== filters.city_cont) {
        handleFilterChange({ city_cont: citySearch || undefined });
      }
    }, 500);

    return () => {
      if (cityTimeoutRef.current) {
        clearTimeout(cityTimeoutRef.current);
      }
    };
  }, [citySearch, filters.city_cont, handleFilterChange]);

  // Debounced state search
  useEffect(() => {
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
    }

    stateTimeoutRef.current = setTimeout(() => {
      if (stateSearch !== filters.state_cont) {
        handleFilterChange({ state_cont: stateSearch || undefined });
      }
    }, 500);

    return () => {
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }
    };
  }, [stateSearch, filters.state_cont, handleFilterChange]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await axios.get<Option[]>("/api/categories");
        setCategories(response.data);

        // No need to preload all subcategories since columns are read-only
      } catch (error) {
        console.error("Failed to load categories:", error);
        setCategories([]);
      }
      setLoadingCategories(false);
    };

    void loadCategories();
  }, []);

  // Load subcategories when category filter changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (!categoryFilter) {
        setSubcategories([]);
        return;
      }

      setLoadingSubcategories(true);
      try {
        const response = await axios.get<Option[]>(`/api/categories/${categoryFilter}`);
        setSubcategories(response.data);
      } catch (error) {
        console.error("Failed to load subcategories:", error);
        setSubcategories([]);
      }
      setLoadingSubcategories(false);
    };

    void loadSubcategories();
  }, [categoryFilter]);

  // categoryOptions no longer needed - columns are read-only

  // Grid subcategory options se actualizarán en beforeeditstart usando subcategorySourceRef

  // Transform suppliers to include id fields for selects
  const transformedSuppliers = suppliers.map((s) => ({
    ...s,
    category_id: s.category?.id ?? 0,
    subcategory_id: s.subcategory?.id ?? 0,
  }));

  // Handle category filter change
  const handleCategoryChange = (value: string) => {
    const categoryId = value === "all" ? undefined : value;
    setCategoryFilter(value === "all" ? "" : value);
    setSubcategoryFilter(""); // Reset subcategory when category changes
    handleFilterChange({
      category_id_eq: categoryId,
      subcategory_id_eq: undefined, // Clear subcategory filter
    });
  };

  // Handle subcategory filter change
  const handleSubcategoryChange = (value: string) => {
    const subcategoryId = value === "all" ? undefined : value;
    setSubcategoryFilter(value === "all" ? "" : value);
    handleFilterChange({ subcategory_id_eq: subcategoryId });
  };

  const clearFilters = () => {
    // Cancel any pending debounced searches
    if (nameTimeoutRef.current) {
      clearTimeout(nameTimeoutRef.current);
      nameTimeoutRef.current = null;
    }
    if (cityTimeoutRef.current) {
      clearTimeout(cityTimeoutRef.current);
      cityTimeoutRef.current = null;
    }
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
      stateTimeoutRef.current = null;
    }

    setNameSearch("");
    setCategoryFilter("");
    setSubcategoryFilter("");
    setCitySearch("");
    setStateSearch("");
    setSupplierTypeFilter("");
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  const toggleSupplierSelection = (supplierId: number) => {
    setSelectedSupplierIds((prev) =>
      prev.includes(supplierId)
        ? prev.filter((id) => id !== supplierId)
        : [...prev, supplierId],
    );
  };

  const selectAllVisibleSuppliers = () => {
    const allVisibleIds = suppliers.map((s) => s.id);
    setSelectedSupplierIds((prev) => {
      const set = new Set(prev);
      allVisibleIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const clearAllSelections = () => {
    setSelectedSupplierIds([]);
  };

  const handleSendQuoteRequest = () => {
    if (selectedSuppliers.size === 0) {
      alert("Por favor, selecciona al menos un proveedor");
      return;
    }

    const supplierIds = Array.from(selectedSuppliers);

    router.post(
      sendInvitationsPurchaseRequestQuoteInvitationsPath(purchase_request.id),
      { supplier_ids: supplierIds },
      {
        onSuccess: () => {
          router.visit(`/purchase_requests/${purchase_request.id}`);
        },
        onError: (errors) => {
          console.error("Error enviando invitaciones:", errors);
        }
      }
    );
  };

  // Calculate grid height to avoid internal scroll
  const gridHeight = computeRevoGridHeight(suppliers.length);

  // RevoGrid column definitions
  const columns: ColumnRegular[] = [
    // Selection checkbox column
    {
      prop: 'selected',
      name: '',
      readonly: true,
      size: 50,
      minSize: 50,
      maxSize: 50,
      pin: 'colPinStart',
      columnTemplate: (createElement: HyperFunc<VNode>) => {
        const allSelected = suppliers.every(s => selectedSuppliers.has(s.id));
        const someSelected = suppliers.some(s => selectedSuppliers.has(s.id)) && !allSelected;

        return createElement('div', {
          className: 'flex items-center justify-center h-full'
        }, [
          createElement('input', {
            type: 'checkbox',
            checked: allSelected || undefined,
            indeterminate: someSelected,
            onChange: allSelected ? clearAllSelections : selectAllVisibleSuppliers,
            className: 'cursor-pointer'
          })
        ]);
      },
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        const isSelected = selectedSuppliers.has(supplier.id);

        return createElement('div', {
          className: 'flex items-center justify-center h-full'
        }, [
          createElement('input', {
            type: 'checkbox',
            checked: isSelected || undefined,
            onChange: () => toggleSupplierSelection(supplier.id),
            className: 'cursor-pointer'
          })
        ]);
      }
    },
    // Supplier name
    {
      prop: 'name',
      name: 'Proveedor',
      readonly: true,
      pin: 'colPinStart',
      size: 250,
      minSize: 200,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        const children = [
          createElement('div', {
            className: 'font-medium text-sm'
          }, supplier.name)
        ];

        if (supplier.notes) {
          children.push(createElement('div', {
            className: 'text-xs text-muted-foreground line-clamp-1'
          }, supplier.notes));
        }

        return createElement('div', {
          className: 'flex flex-col justify-center h-full px-2'
        }, children);
      }
    },
    {
      prop: 'supplier_type',
      name: 'Tipo de proveedor',
      readonly: true,
      size: 180,
      minSize: 150,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        return createElement('div', {
          className: 'flex items-center h-full px-2 text-sm text-muted-foreground'
        }, supplier.supplier_type ?? '—');
      }
    },
    // Location
    {
      prop: 'location',
      name: 'Ubicación',
      readonly: true,
      size: 180,
      minSize: 150,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        if (supplier.city && supplier.state) {
          return createElement('div', {
            className: 'flex items-center gap-1 h-full px-2 text-sm text-muted-foreground'
          }, [
            createElement('span', {
              className: 'text-xs'
            }, '📍'),
            createElement('span', {}, `${supplier.city}, ${supplier.state}`)
          ]);
        }
        return createElement('div', {
          className: 'flex items-center h-full px-2 text-sm text-muted-foreground'
        }, '-');
      }
    },
    // Email - editable text with anchor display
    {
      prop: 'email',
      name: 'Email',
      size: 200,
      minSize: 150,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const email = String(cellProps.value ?? '');
        if (email) {
          return createElement('a', {
            href: `mailto:${email}`,
            className: 'text-blue-600 hover:underline text-sm'
          }, email);
        }
        return createElement('span', {
          className: 'text-muted-foreground text-sm'
        }, '—');
      }
    },
    // Phone - editable text
    {
      prop: 'phone',
      name: 'Teléfono',
      size: 130,
      minSize: 120,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const phone = String(cellProps.value ?? '');
        const displayText = phone || '—';
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${phone ? '' : 'text-muted-foreground'}`
        }, displayText);
      }
    },
    // Category - read-only display
    {
      prop: 'category_id',
      name: 'Categoría',
      readonly: true,
      size: 160,
      minSize: 140,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const model = cellProps.model as Supplier;
        const displayText = model.category?.name ?? '—';
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${model.category?.name ? '' : 'text-muted-foreground'}`
        }, displayText);
      }
    },
    // Subcategory - read-only display
    {
      prop: 'subcategory_id',
      name: 'Subcategoría',
      readonly: true,
      size: 170,
      minSize: 140,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const model = cellProps.model as Supplier;
        const displayText = model.subcategory?.name ?? '—';
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${model.subcategory?.name ? '' : 'text-muted-foreground'}`
        }, displayText);
      }
    },
    // Purchase condition (e.g., contado, 30 días, anticipo)
    {
      prop: 'purchase_condition',
      name: 'Condición de compra',
      readonly: true,
      size: 180,
      minSize: 160,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        const value = supplier.purchase_condition;
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${value ? '' : 'text-muted-foreground'}`
        }, value ?? '—');
      }
    },
    // Contact person
    {
      prop: 'contact_person',
      name: 'Contacto (Persona)',
      readonly: true,
      size: 180,
      minSize: 150,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        const contactPerson = supplier.contact_person;
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${contactPerson ? '' : 'text-muted-foreground'}`
        }, contactPerson ?? '—');
      }
    },
    // Website link
    {
      prop: 'website',
      name: 'Web',
      readonly: true,
      size: 120,
      minSize: 110,
      maxSize: 160,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const supplier = cellProps.model as Supplier;
        const url = supplier.website;
        if (!url) {
          return createElement('div', { className: 'flex items-center h-full px-2 text-sm text-muted-foreground' }, '—');
        }
        return createElement('a', {
          href: url.startsWith('http') ? url : `https://${url}`,
          target: '_blank',
          rel: 'noreferrer',
          className: 'text-blue-600 hover:underline text-sm px-2 truncate'
        }, 'Sitio');
      }
    }
  ];

  // Register column plugins
  const columnTypes = {
    select: new SelectTypePlugin(),
  };

  // Helper function for subcategory placeholder
  const getSubcategoryPlaceholder = () => {
    if (!categoryFilter) return "Selecciona categoría";
    if (loadingSubcategories) return "Cargando...";
    return "Subcategoría";
  };

  // Enable cell editing and patch to backend
  useRevoGridEdit<Supplier>({
    gridRef,
    pathGenerator: (supplier) => supplierPath(supplier),
    resourceKey: 'supplier',
    valueProcessor: (prop, newValue) => {
      if (prop === 'category_id' || prop === 'subcategory_id') {
        return (newValue === 0 || newValue === '0' || newValue === undefined) ? null : Number(newValue);
      }
      return newValue as string | number | null;
    }
  });

  // No need for beforeeditstart handler - columns are read-only

  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    {
      title: `Solicitud #${purchase_request.request_number}`,
      href: purchaseRequestPath(purchase_request),
    },
    {
      title: "Seleccionar Proveedores",
      href: "#",
    },
  ];

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head
        title={`Nueva Solicitud de Cotización - Solicitud #${purchase_request?.request_number}`}
      />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={purchaseRequestPath(purchase_request)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Solicitud
              </Link>
            </Button>
          </div>
        </div>

        {/* Purchase Request Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Información de la Solicitud
            </CardTitle>
            <CardDescription>
              Revisa los detalles de la solicitud antes de enviar las
              cotizaciones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Número
                </label>
                <p className="font-medium">{purchase_request.request_number}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Fecha
                </label>
                <p className="font-medium">{purchase_request.request_date}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Sector
                </label>
                <p className="font-medium">{purchase_request.sector.name}</p>
              </div>
              {purchase_request.requester && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Solicitante
                  </label>
                  <p className="font-medium">{purchase_request.requester}</p>
                </div>
              )}
              {purchase_request.authorizer && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Autorizador
                  </label>
                  <p className="font-medium">{purchase_request.authorizer}</p>
                </div>
              )}
              {purchase_request.priority && (
                <div>
                  <Badge variant="destructive">Prioritario</Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Supplier Selection Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Seleccionar Proveedores para Cotización
                </CardTitle>
                <CardDescription>
                  {selectedSuppliers.size > 0
                    ? `${selectedSuppliers.size} proveedor(es) seleccionado(s) de ${pagy.count} total`
                    : `Total: ${pagy.count} proveedores disponibles`}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {selectedSuppliers.size > 0 && (
                  <Button
                    onClick={handleSendQuoteRequest}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Solicitud ({selectedSuppliers.size})
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected Suppliers Chips */}
            <SelectedSuppliersChips
              selectedSupplierIds={selectedSuppliers}
              allSuppliers={chipSuppliers}
              onRemoveSupplier={toggleSupplierSelection}
              onClearAll={clearAllSelections}
            />

            {/* Filters */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Input
                  placeholder="Buscar por nombre..."
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  className="w-64"
                />

                <Select
                  value={categoryFilter || "all"}
                  onValueChange={handleCategoryChange}
                  disabled={loadingCategories}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue
                      placeholder={
                        loadingCategories
                          ? "Cargando..."
                          : "Categoría"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categories.map((category) => (
                      <SelectItem
                        key={category.value}
                        value={String(category.value)}
                      >
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={subcategoryFilter || "all"}
                  onValueChange={handleSubcategoryChange}
                  disabled={loadingSubcategories || !categoryFilter}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={getSubcategoryPlaceholder()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las subcategorías</SelectItem>
                    {subcategories.map((subcategory) => (
                      <SelectItem
                        key={subcategory.value}
                        value={String(subcategory.value)}
                      >
                        {subcategory.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={supplierTypeFilter || "all"}
                  onValueChange={(value) => {
                    const v = value === "all" ? undefined : value;
                    setSupplierTypeFilter(value === "all" ? "" : value);
                    handleFilterChange({ supplier_type_eq: v });
                  }}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Tipo de proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="distribuidor">Distribuidor</SelectItem>
                    <SelectItem value="fabricante">Fabricante</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <SearchableSelect
                  options={cities}
                  value={citySearch}
                  onValueChange={setCitySearch}
                  placeholder="Ciudad..."
                  searchPlaceholder="Buscar ciudad..."
                  emptyMessage="No se encontraron ciudades"
                  className="w-44"
                />

                <SearchableSelect
                  options={states}
                  value={stateSearch}
                  onValueChange={setStateSearch}
                  placeholder="Provincia..."
                  searchPlaceholder="Buscar provincia..."
                  emptyMessage="No se encontraron provincias"
                  className="w-44"
                />

                {(nameSearch ||
                  categoryFilter ||
                  subcategoryFilter ||
                  citySearch ||
                  stateSearch ||
                  supplierTypeFilter) && (
                    <Button variant="outline" size="sm" onClick={clearFilters}>
                      <Trash2 className="h-4 w-4" /> Limpiar filtros
                    </Button>
                  )}
              </div>
            </div>


            {/* Suppliers Grid */}
            {suppliers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No se encontraron proveedores con los filtros aplicados</p>
                  {(nameSearch ||
                  categoryFilter ||
                  subcategoryFilter ||
                  citySearch ||
                  stateSearch) && (
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                      className="mt-4"
                    >
                      <Trash2 className="h-4 w-4" /> Limpiar filtros
                    </Button>
                  )}
              </div>
            ) : (
              <div className={`min-h-[400px] ${isLoading ? "opacity-50" : ""}`}>
                <div className="text-xs text-muted-foreground mb-2">
                  Haz doble clic en la celda que quieras editar (email, teléfono, categoría o subcategoría).
                </div>
                <RevoGrid
                  ref={gridRef}
                  source={transformedSuppliers}
                  columns={columns}
                  columnTypes={columnTypes}
                  theme="material"
                  hideAttribution
                  stretch={true}
                  autoSizeColumn={true}
                  style={{ height: `${gridHeight}px` }}
                />
              </div>
            )}

            {/* Pagination */}
            {suppliers.length > 0 && (
              <DataTablePagination
                pagy={pagy}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                entityName="proveedores"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
